import { AfterViewInit, Component, Input, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ApexAnnotations, ApexAxisChartSeries, ApexChart, ApexTooltip, ApexXAxis, ApexYAxis, ChartComponent } from 'ng-apexcharts';
import { CalibrationDetectorDTO } from 'src/app/model/calibration-detector-dto';
import { LoadIndicatorService } from 'src/app/services/load-indicator.service';
import { SensorService } from 'src/app/services/sensor.service';
import { NodeDiagnosisInfoComponent, NodeDiagnosisInfoData } from './node-diagnosis-info/node-diagnosis-info.component';

export type ChartOptions = {
    series: ApexAxisChartSeries;
    chart: ApexChart;
    xaxis: ApexXAxis;
    yaxis: ApexYAxis;
    tooltip: ApexTooltip;
    annotations: ApexAnnotations;
    title: ApexTitleSubtitle;
};

@Component({
    selector: 'app-node-diagnosis',
    templateUrl: './node-diagnosis.component.html',
    styleUrls: ['./node-diagnosis.component.scss']
})
export class NodeDiagnosisComponent implements AfterViewInit {

    @Input() nodeId?: number;
    @Input() nodeIdentifier?: string;

    @ViewChild("chart") chart!: ChartComponent;
    public chartOptions: Partial<ChartOptions> = {};

    public fromDate?: string
    public toDate?: string
    public lastRefresh: Date = new Date();
    public anomalies: CalibrationDetectorDTO[] = [];
    public tabChart: number = 1
    public tabTable: number = 2
    public currentTab = this.tabChart

    constructor(private readonly sensorService: SensorService, private readonly loadIndicatorService: LoadIndicatorService, private readonly dialog: MatDialog) {

    }

    ngAfterViewInit(): void {
        if (!!this.nodeId && !!this.nodeIdentifier) {
            this.getObservations();
        }
    }

    getObservations() {
        this.lastRefresh = new Date();
        this.loadIndicatorService.show()
        this.sensorService.getObservations(this.nodeIdentifier!, this.fromDate, this.toDate).then(response => {
            this.anomalies = response.filter(e => e.features.split(',').length != 0)
            this.anomalies.forEach(x => {
                x.dStatistic = +x.dStatistic.toFixed(3)
                x.dStatisticUCL = +x.dStatisticUCL.toFixed(3)
                x.qStatistic = +x.qStatistic.toFixed(3)
                x.qStatisticUCL = +x.qStatisticUCL.toFixed(3)
            })
            this.initializeChart(response)
        }).finally(() => {
            this.loadIndicatorService.hide()
        });
    }

    initializeChart(data: CalibrationDetectorDTO[]) {
        let qstArray: any[] = [];
        let dstArray: any[] = [];
        let ucld = undefined;
        let uclq = undefined;
        data.forEach(element => {
            let date = new Date(element.timestamp);
            qstArray.push([date.getTime(), element.qStatistic]);
            dstArray.push([date.getTime(), element.dStatistic]);
            ucld = element.dStatisticUCL;
            uclq = element.qStatisticUCL;
        });
        this.chartOptions = {
            series: [
                {
                    name: "Qst",
                    data: qstArray
                },
                {
                    name: "Dst",
                    data: dstArray
                }
            ],
            title: {
                text: "Observations",
                align: "left"
            },
            chart: {
                height: 350,
                type: 'scatter',
                zoom: {
                    enabled: true,
                    type: 'xy'
                },
                width: '100%',
                toolbar: {
                    tools: {
                        pan: false
                    }
                },
                fontFamily: 'monospace'
            },
            xaxis: {
                type: 'datetime',
                labels: {
                    formatter: function (value) {
                        const date = new Date(value);
                        return `${date.toISOString().split('T')[0]} ${date.toISOString().split('T')[1].split('.')[0]}`;
                    }
                }
            },
            yaxis: {
                labels: {
                    formatter: function (value) {
                        if (Math.abs(value) < 0.001) {
                            return value.toExponential(2);
                        } else {
                            return value.toFixed(2);
                        }
                    }
                }
            },
            tooltip: {
                x: {
                    format: 'yyyy-MM-dd'
                }
            },
            annotations: {
                yaxis: [
                    {
                        y: uclq,
                        borderColor: '#FF0000',
                        label: {
                            text: `UCLq: ${uclq}`,
                            style: {
                                color: '#fff',
                                background: '#FF0000'
                            }
                        }
                    },
                    {
                        y: ucld,
                        borderColor: '#0000FF',
                        label: {
                            text: `UCLd: ${ucld}`,
                            style: {
                                color: '#fff',
                                background: '#0000FF'
                            }
                        }
                    }
                ]
            }
        };
    }

    onInfoClicked(anomaly: CalibrationDetectorDTO) {
        this.dialog.open(NodeDiagnosisInfoComponent, {
            data: { node: this.nodeId, anomaly } as NodeDiagnosisInfoData,
        });
    }
}
